/**
 * ACAD_TABLE serializer (§3.2 decision 8). The table is no longer reduced
 * to grid + texts: it exports as a native ACAD_TABLE (AcDbBlockReference +
 * AcDbTable) with row/column counts, row heights, column widths and one
 * typed (171=1 text) cell per table cell. The OBJECTS-side table content
 * object is deliberately skipped in Faz 1 (AutoCAD tolerates its absence;
 * see the phase risk notes).
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';
import { tableCellDisplay } from '../../../../core/commands/tableModel';

export function writeTable(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    const cells = Array.isArray(d.cells) ? (d.cells as string[][]) : [];
    if (cells.length === 0) return;
    const pos = (Array.isArray(d.position) ? d.position : [0, 0, 0]) as number[];
    const colWidth = typeof d.colWidth === 'number' && d.colWidth > 0 ? d.colWidth : 2;
    const rowHeight = typeof d.rowHeight === 'number' && d.rowHeight > 0 ? d.rowHeight : 0.6;
    const cols = cells.reduce((m, r) => Math.max(m, r.length), 0);
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;

    entityPrologue(w, ctx, e, 'ACAD_TABLE', owner);
    w.pair(100, 'AcDbBlockReference');
    w.pair(10, pos[0] ?? 0).pair(20, pos[1] ?? 0).pair(30, pos[2] ?? 0);
    if (rot) w.pair(50, ((rot % 360) + 360) % 360);
    w.pair(100, 'AcDbTable');
    w.pair(2, ''); // anonymous graphics block — none generated in Faz 1
    w.pair(343, ctx.modelSpaceRecord); // block table record the table lives in
    w.pair(90, 0); // table value flags
    w.pair(91, cells.length); // row count
    w.pair(92, cols); // column count
    for (let r = 0; r < cells.length; r++) w.pair(141, rowHeight);
    for (let c = 0; c < cols; c++) w.pair(142, colWidth);
    cells.forEach((row, r) => {
        for (let c = 0; c < cols; c++) {
            // AutoCAD has no yap formula dialect: the EVALUATED value is
            // written (Faz 8.6 parity note — formulas flatten on export).
            const text = tableCellDisplay(d, r, c);
            w.pair(171, 1); // cell type: text
            w.pair(172, 0); // cell flags
            w.pair(174, 0).pair(175, 0); // merged extents
            w.pair(178, 0); // content alignment
            w.pair(300, text);
            void r;
        }
    });
}
